import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Message } from 'semantic-ui-react';
import { CodeGroupDto, UpsertCodeDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import { addCode, editCode } from 'services/codeServices';
import Select from 'components/final-form/Select';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  codeId?: number,
  code?: UpsertCodeDto,
  codeGroups: Array<CodeGroupDto>
}

interface State {
  code?: Partial<UpsertCodeDto>,
  codeGroupOptions: Array<DropdownOption>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

let initialCodeForm: Partial<UpsertCodeDto> = {
  description: "",
  codeId: "",
  codeGroupId: -1,
  deleted: false
};

class UpsertCodeForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {code, codeGroups} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    initialCodeForm.codeGroupId = codeGroups[0].id;

    let codeGroupOptions = codeGroups.filter(codeGroup => !codeGroup.deleted);

    if (code) {
      codeGroupOptions = [];
      codeGroups.forEach(codeGroup => {
        if (codeGroup.id === code.codeGroupId || !codeGroup.deleted) {
          codeGroupOptions.push(codeGroup);
        }
      })
    }

    this.state = {
      code: code ? code : initialCodeForm,
      codeGroupOptions: mapToDropdownOptionArray(codeGroupOptions, "description"),
      cancelTokenSource
    };
  };

  handleSubmit = async (values: Partial<UpsertCodeDto>) => {

    const {codeId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    if (codeId) {
      await editCode(codeId, values, cancelTokenSource)
          .then(onSave)
          .catch(error => this.handleError(error))
    } else {
      await addCode(values, cancelTokenSource)
          .then(onSave)
          .catch(error => this.handleError(error))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    this.setState({
      errorMessage: getMessage('error.general')
    });
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {code} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={code}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {codeGroupOptions, errorMessage} = this.state;
    const {getMessage, onCancel} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
                <PraxFormGrid>
                  {errorMessage && this.renderErrorSegment()}
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("description")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="description"
                          component={Input}
                          validate={required}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("code.codeId")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="codeId"
                          component={Input}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("code.group")}
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          fluid
                          name="codeGroupId"
                          component={Select}
                          options={codeGroupOptions}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel} >{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

}

export default withMessages(UpsertCodeForm);
